import { RequestKind, boolean, string } from '@sb/library/base';
import {
    BatchRepertoireAssignmentUpdateItems,
    BatchRepertoireUpdateItems,
    DisplayItemCollection,
    RepertoireAssignmentDisplayItem,
    RepertoireAssignmentInputItem,
    RepertoireAssignmentSearchOptions,
    RepertoireAssignmentUpdateItem,
    RepertoireDisplayItem,
    RepertoireInputItem,
    RepertoireMergeInputItem,
    RepertoirePerformanceDisplayItem,
    RepertoirePerformanceInputItem,
    RepertoirePerformanceSearchOptions,
    RepertoirePerformanceUpdateItem,
    RepertoireSearchOptions,
    RepertoireUpdateItem
} from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace RepertoireRoutes {
    export namespace Composers{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/composers',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export namespace Genres{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/genres',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export namespace Instruments{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/instruments',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export namespace Sources{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/sources',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export namespace Languages{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/languages',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export namespace Difficulties{
        export const Get = ApiRoute.Define({
            URL: 'repertoire/difficulties',
            Kind: RequestKind.Get,
            ResultType: Array<string>
        });
    }

    export const Add = ApiRoute.Define({
        URL: 'repertoire',
        Kind: RequestKind.Post,
        BodyType: RepertoireInputItem,
        ResultType: string
    });

    export const Get = ApiRoute.Define({
        URL: (repertoireID: string) => `repertoire/${repertoireID}/`,
        Kind: RequestKind.Get,
        ResultType: RepertoireDisplayItem
    });

    export const GetAll = ApiRoute.Define({
        URL: 'repertoire',
        Kind: RequestKind.Get,
        ResultType: DisplayItemCollection<RepertoireDisplayItem>
    });

    export const Delete = ApiRoute.Define({
        URL: (repertoireID: string) => `repertoire/${repertoireID}/`,
        Kind: RequestKind.Delete,
        ResultType: boolean
    });

    export const Update = ApiRoute.Define({
        URL: (repertoireID: string) => `repertoire/${repertoireID}/`,
        Kind: RequestKind.Put,
        BodyType: RepertoireUpdateItem,
        ResultType: boolean
    });

    export const Batch = ApiRoute.Define({
        URL: 'repertoire',
        Kind: RequestKind.Patch,
        BodyType: BatchRepertoireUpdateItems,
        ResultType: boolean
    });

    export const Search = ApiRoute.Define({
        URL: 'search/repertoire',
        Kind: RequestKind.Post,
        BodyType: RepertoireSearchOptions,
        PagingAllowed: true,
        ResultType: DisplayItemCollection<RepertoireDisplayItem>
    });

    export const SearchAsSubstitute = ApiRoute.Define({
        URL: (eventID: string) => `search/event/${eventID}/repertoire`,
        Kind: RequestKind.Post,
        BodyType: RepertoireSearchOptions,
        ResultType: DisplayItemCollection<RepertoireDisplayItem>
    });

    export const Merge = ApiRoute.Define({
        URL: `repertoire/merge`,
        Kind: RequestKind.Post,
        BodyType: RepertoireMergeInputItem,
        ResultType: boolean
    });

    export namespace Assignment {
        export const Add = ApiRoute.Define({
            URL: (studentID: string, repertoireID: string) => `students/${studentID}/repertoire/${repertoireID}`,
            Kind: RequestKind.Post,
            BodyType: RepertoireAssignmentInputItem,
            ResultType: boolean
        });

        export const AddFromEvent = ApiRoute.Define({
            URL: (eventID: string, studentID: string, repertoireID: string) => `events/${eventID}/students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Post,
            BodyType: RepertoireAssignmentInputItem,
            ResultType: boolean
        });

        export const Update = ApiRoute.Define({
            URL: (studentID: string, repertoireID: string) => `students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Put,
            BodyType: RepertoireAssignmentUpdateItem,
            ResultType: boolean
        });

        export const UpdateFromEvent = ApiRoute.Define({
            URL: (eventID: string, studentID: string, repertoireID: string) => `events/${eventID}/students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Put,
            BodyType: RepertoireAssignmentUpdateItem,
            ResultType: boolean
        });

        export const Batch = ApiRoute.Define({
            URL: `repertoire/assignments`,
            Kind: RequestKind.Patch,
            BodyType: BatchRepertoireAssignmentUpdateItems,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (studentID: string, repertoireID: string) => `students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Get = ApiRoute.Define({
            URL: (studentID: string, repertoireID: string) => `students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Get,
            ResultType: RepertoireAssignmentDisplayItem
        });

        export const GetForAttendingStudent = ApiRoute.Define({
            URL: (eventID: string, studentID: string, repertoireID: string) => `events/${eventID}/students/${studentID}/repertoire/${repertoireID}/`,
            Kind: RequestKind.Get,
            ResultType: RepertoireAssignmentDisplayItem
        });

        export const Search = ApiRoute.Define({
            URL: `search/repertoire/assignments/`,
            Kind: RequestKind.Post,
            BodyType: RepertoireAssignmentSearchOptions,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<RepertoireAssignmentDisplayItem>
        });

        export const SearchForEvent = ApiRoute.Define({
            URL: (eventID: string) => `search/event/${eventID}/repertoire/assignments/`,
            Kind: RequestKind.Post,
            PagingAllowed: true,
            BodyType: RepertoireAssignmentSearchOptions,
            ResultType: DisplayItemCollection<RepertoireAssignmentDisplayItem>
        });
    }

    export namespace Performance {
        export const Get = ApiRoute.Define({
            URL: (repertoirePerformanceID: string) => `repertoire/performances/${repertoirePerformanceID}`,
            Kind: RequestKind.Get,
            ResultType: RepertoirePerformanceDisplayItem
        });

        export const Search = ApiRoute.Define({
            URL: `search/repertoire/performances`,
            Kind: RequestKind.Post,
            PagingAllowed: true,
            BodyType: RepertoirePerformanceSearchOptions,
            ResultType: DisplayItemCollection<RepertoirePerformanceDisplayItem>
        });

        export const Add = ApiRoute.Define({
            URL: `repertoire/performances`,
            Kind: RequestKind.Post,
            BodyType: RepertoirePerformanceInputItem,
            ResultType: boolean
        });

        export const Update = ApiRoute.Define({
            URL: (repertoirePerformanceID: string) => `repertoire/performances/${repertoirePerformanceID}`,
            Kind: RequestKind.Put,
            BodyType: RepertoirePerformanceUpdateItem,
            ResultType: boolean
        });

        export const Delete = ApiRoute.Define({
            URL: (repertoirePerformanceID: string) => `repertoire/performances/${repertoirePerformanceID}/`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });
    }
}
